import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageSquare, Send, Clock, CheckCircle, AlertCircle, X, ChevronDown, ChevronUp, User, Shield } from 'lucide-react';

const STATUS_CONFIG = {
  open: { label: 'Open', cls: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  pending: { label: 'Pending', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  resolved: { label: 'Resolved', cls: 'bg-green-500/15 text-green-400 border-green-500/30' },
  closed: { label: 'Closed', cls: 'bg-[var(--bg-primary)] text-[var(--text-tertiary)] border-[var(--border-subtle)]' },
};

const FILTERS = ['all', 'open', 'pending', 'resolved', 'closed'];

function TicketCard({ ticket, onReply, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const sc = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;

  const handleReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await onReply(ticket.id, reply);
      setReply('');
    } catch (e) { toast.error('Failed to send reply'); }
    setSending(false);
  };

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg" data-testid={`ticket-${ticket.id}`}>
      <button onClick={() => setExpanded(v => !v)} className="w-full flex items-center justify-between p-4 text-left hover:bg-[var(--bg-primary)]/50 transition-colors rounded-t-lg">
        <div className="flex items-center gap-3 min-w-0">
          <MessageSquare className="w-4 h-4 text-cyan-400 shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-[var(--text-primary)]">{ticket.ticket_number}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${sc.cls}`}>{sc.label}</span>
              <span className="text-[10px] text-[var(--text-tertiary)] bg-[var(--bg-primary)] px-2 py-0.5 rounded">{ticket.category}</span>
              <span className="text-[10px] text-[var(--text-tertiary)]">{ticket.priority}</span>
            </div>
            <p className="text-xs text-[var(--text-secondary)] truncate mt-0.5">{ticket.subject}</p>
            <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
              {ticket.user_name} ({ticket.user_email}) — {new Date(ticket.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] text-[var(--text-tertiary)]">{ticket.messages?.length || 0} msgs</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-[var(--text-tertiary)]" /> : <ChevronDown className="w-4 h-4 text-[var(--text-tertiary)]" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[var(--border-subtle)] p-4 space-y-3">
          {/* Messages */}
          <div className="max-h-64 overflow-y-auto space-y-2" data-testid="ticket-messages">
            {(ticket.messages || []).map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.sender === 'admin' ? 'flex-row-reverse' : ''}`}>
                <div className={`p-1.5 rounded-full shrink-0 ${msg.sender === 'admin' ? 'bg-cyan-500/15' : 'bg-[var(--bg-primary)]'}`}>
                  {msg.sender === 'admin' ? <Shield className="w-3 h-3 text-cyan-400" /> : <User className="w-3 h-3 text-[var(--text-tertiary)]" />}
                </div>
                <div className={`max-w-[75%] p-2.5 rounded-lg ${msg.sender === 'admin' ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-[var(--bg-primary)] border border-[var(--border-subtle)]'}`}>
                  <p className="text-xs text-[var(--text-primary)]">{msg.message}</p>
                  <p className="text-[10px] text-[var(--text-tertiary)] mt-1">
                    {msg.sender === 'admin' ? (msg.admin_name || 'Admin') : 'User'} — {new Date(msg.timestamp).toLocaleString('en-IN', { hour12: false, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Reply + Status */}
          <div className="flex gap-2">
            <Input data-testid="ticket-reply-input" placeholder="Type your reply..." value={reply} onChange={e => setReply(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleReply()}
              className="flex-1 bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] text-xs" />
            <Button data-testid="ticket-reply-btn" onClick={handleReply} disabled={sending || !reply.trim()} size="sm"
              className="bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/25">
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {['open', 'pending', 'resolved', 'closed'].map(s => {
              const cfg = STATUS_CONFIG[s];
              return (
                <Button key={s} size="sm" variant="outline" disabled={ticket.status === s}
                  data-testid={`ticket-status-${s}`}
                  onClick={() => onStatusChange(ticket.id, s)}
                  className={`text-[10px] h-7 px-2 border ${ticket.status === s ? cfg.cls : 'border-[var(--border-subtle)] text-[var(--text-tertiary)]'}`}>
                  {s === 'open' && <AlertCircle className="w-3 h-3 mr-1" />}
                  {s === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                  {s === 'resolved' && <CheckCircle className="w-3 h-3 mr-1" />}
                  {s === 'closed' && <X className="w-3 h-3 mr-1" />}
                  {cfg.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await adminAPI.getTickets(filter);
      setTickets(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleReply = async (ticketId, message) => {
    await adminAPI.replyTicket(ticketId, { message });
    toast.success('Reply sent');
    await fetchTickets();
  };

  const handleStatusChange = async (ticketId, status) => {
    await adminAPI.updateTicketStatus(ticketId, status);
    toast.success(`Status updated to ${status}`);
    await fetchTickets();
  };

  const counts = {
    all: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    pending: tickets.filter(t => t.status === 'pending').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    closed: tickets.filter(t => t.status === 'closed').length,
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-tickets-page">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Support Tickets</h1>
          <p className="text-sm text-[var(--text-secondary)]">Manage and respond to user tickets</p>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 flex-wrap" data-testid="ticket-filters">
          {FILTERS.map(f => (
            <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'}
              data-testid={`filter-${f}`}
              onClick={() => { setFilter(f); setLoading(true); }}
              className={filter === f ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30' : 'border-[var(--border-subtle)] text-[var(--text-secondary)]'}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
              <span className="ml-1 text-[10px] opacity-60">({counts[f]})</span>
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-[var(--text-tertiary)]">Loading tickets...</div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-10 h-10 text-[var(--text-tertiary)] mx-auto mb-3 opacity-30" />
            <p className="text-sm text-[var(--text-tertiary)]">No tickets found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map(t => (
              <TicketCard key={t.id} ticket={t} onReply={handleReply} onStatusChange={handleStatusChange} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
